/**
 * ContactPage - Contact form, contact details, and My Tickets (tabbed)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../utils/firebase-config';
import { createContactTicket, trackContactTicket, sendContactTicketMessage, uploadTicketFiles } from '../services/support';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
} from 'firebase/firestore';
import './labcss/contact.css';
import './labcss/my-tickets.css';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';

export default function ContactPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initial tab from URL query param: ?tab=tickets or ?tab=tracking
  const searchParams = new URLSearchParams(location.search);
  let initialTab = 'contact';
  if (searchParams.get('tab') === 'tickets' && user) {
    initialTab = 'tickets';
  } else if (searchParams.get('tab') === 'tracking') {
    initialTab = 'tracking';
  }

  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [error, setError] = useState(null);
  const [contactAttachments, setContactAttachments] = useState([]);
  const contactFileInputRef = useRef(null);

  // ── Tickets state ──
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Tracking form state
  const [trackingEmail, setTrackingEmail] = useState('');
  const [trackingCode, setTrackingCode] = useState(searchParams.get('code') || '');
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [trackedTicket, setTrackedTicket] = useState(null);
  const [trackedCredentials, setTrackedCredentials] = useState(null);
  const [trackedMessages, setTrackedMessages] = useState([]);
  const [trackedNewMessage, setTrackedNewMessage] = useState('');
  const [trackedSending, setTrackedSending] = useState(false);
  const trackedMessagesEndRef = useRef(null);
  const trackedMessagesAreaRef = useRef(null);
  const [trackedAttachments, setTrackedAttachments] = useState([]);
  const [trackedUploading, setTrackedUploading] = useState(false);
  const trackedFileInputRef = useRef(null);

  // Constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime', 'application/pdf'];

  const handleFileSelect = (files, setAttachmentsFn) => {
    const fileArray = Array.from(files);
    const valid = [];
    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`"${file.name}" is too large. Maximum size is 5 MB.`);
        continue;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`"${file.name}" is not supported. Allowed: images, videos, PDF.`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length > 0) {
      setAttachmentsFn(prev => [...prev, ...valid].slice(0, 5)); // max 5 files
    }
  };

  const uploadAttachments = uploadTicketFiles;

  // Load user's tickets (by userId AND by email, merged & deduplicated)
  useEffect(() => {
    if (!user || activeTab !== 'tickets') return;

    const colRef = collection(db, 'contactSubmissions');

    // Query 1: tickets created while logged in (have userId)
    const qByUid = query(colRef, where('userId', '==', user.uid), orderBy('createdAt', 'desc'));

    // Query 2: tickets created while logged out but with the same email
    const qByEmail = query(colRef, where('email', '==', user.email), orderBy('createdAt', 'desc'));

    let uidData = [];
    let emailData = [];
    let received = 0;

    const merge = () => {
      // Deduplicate by doc id, prefer uid-matched entry
      const map = new Map();
      uidData.forEach((t) => map.set(t.id, t));
      emailData.forEach((t) => { if (!map.has(t.id)) map.set(t.id, t); });
      const merged = Array.from(map.values()).sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setTickets(merged);
      setTicketsLoading(false);

      setSelectedTicket(current => current ? (merged.find(ticket => ticket.id === current.id) || current) : null);
    };

    const unsub1 = onSnapshot(qByUid, (snapshot) => {
      uidData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      received++;
      if (received >= 2) merge();
    });

    const unsub2 = onSnapshot(qByEmail, (snapshot) => {
      emailData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      received++;
      if (received >= 2) merge();
    });

    return () => { unsub1(); unsub2(); };
  }, [user, activeTab]);

  const selectedTicketId = selectedTicket?.id;
  const selectedTicketHasUnreadReply = selectedTicket?.hasUnreadReply;

  // Load messages for selected ticket
  useEffect(() => {
    if (!selectedTicketId) { setMessages([]); return; }

    const q = query(
      collection(db, 'contactSubmissions', selectedTicketId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsub = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      if (selectedTicketHasUnreadReply) {
        updateDoc(doc(db, 'contactSubmissions', selectedTicketId), {
          hasUnreadReply: false,
        }).catch(() => {});
      }
    });

    return () => unsub();
  }, [selectedTicketId, selectedTicketHasUnreadReply]);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Guest tracking is a credential-validated callable; Firestore tickets stay private.
  useEffect(() => {
    if (!trackedTicket?.id || !trackedCredentials) return;
    let active = true;
    const refresh = async () => {
      try {
        const result = await trackContactTicket({ ticketId: trackedTicket.id, ...trackedCredentials });
        if (active) { setTrackedTicket(result.ticket); setTrackedMessages(result.messages); }
      } catch (err) { if (active) setTrackingError(err.message || 'Unable to refresh this ticket.'); }
    };
    const interval = setInterval(refresh, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [trackedTicket?.id, trackedCredentials]);

  useEffect(() => {
    if (trackedMessagesAreaRef.current) {
      trackedMessagesAreaRef.current.scrollTop = trackedMessagesAreaRef.current.scrollHeight;
    }
  }, [trackedMessages]);

  // ── Helpers ──
  // ── Handlers ──

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const created = await createContactTicket(formData);
      if (contactAttachments.length > 0) {
        try {
          const credentials = { email: created.email, trackingCode: created.trackingCode };
          const uploadedFiles = await uploadAttachments(contactAttachments, created.id, credentials);
          await sendContactTicketMessage({ ticketId: created.id, ...credentials, text: '', attachments: uploadedFiles });
        } catch (attachmentError) {
          setError('Your ticket was created, but attachments could not be uploaded. Use ticket tracking to retry.');
        }
      }

      setContactAttachments([]);
      setSubmittedData(created);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !selectedTicket) return;

    setSending(true);
    setUploading(attachments.length > 0);
    try {
      let uploadedFiles = [];
      if (attachments.length > 0) {
        uploadedFiles = await uploadAttachments(attachments, selectedTicket.id);
      }

      await sendContactTicketMessage({ ticketId: selectedTicket.id, text: newMessage.trim(), attachments: uploadedFiles });

      setNewMessage('');
      setAttachments([]);
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSearchTrackedTicket = async (e) => {
    e.preventDefault();
    setTrackingError(null);
    setTrackingLoading(true);
    setTrackedTicket(null);
    setTrackedMessages([]);

    try {
      if (!trackingEmail.trim() || !trackingCode.trim()) {
        setTrackingError('Please enter both email and tracking code');
        return;
      }

      const credentials = { email: trackingEmail.toLowerCase().trim(), trackingCode: trackingCode.toUpperCase().trim() };
      const result = await trackContactTicket(credentials);
      setTrackedCredentials(credentials);
      setTrackedTicket(result.ticket);
      setTrackedMessages(result.messages);
    } catch (err) {
      console.error('Error searching ticket:', err);
      setTrackingError('Failed to search ticket. Please try again.');
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleSendTrackedMessage = async (e) => {
    e.preventDefault();
    if ((!trackedNewMessage.trim() && trackedAttachments.length === 0) || !trackedTicket) return;

    setTrackedSending(true);
    setTrackedUploading(trackedAttachments.length > 0);
    try {
      let uploadedFiles = [];
      if (trackedAttachments.length > 0) {
        uploadedFiles = await uploadAttachments(trackedAttachments, trackedTicket.id, trackedCredentials);
      }

      await sendContactTicketMessage({ ticketId: trackedTicket.id, ...trackedCredentials,
        text: trackedNewMessage.trim(), attachments: uploadedFiles });
      const refreshed = await trackContactTicket({ ticketId: trackedTicket.id, ...trackedCredentials });
      setTrackedTicket(refreshed.ticket);
      setTrackedMessages(refreshed.messages);
      setTrackedNewMessage('');
      setTrackedAttachments([]);
    } catch (err) {
      console.error('Error sending message:', err);
      setTrackingError('Failed to send message.');
    } finally {
      setTrackedSending(false);
      setTrackedUploading(false);
    }
  };

  const handleKeyDownTracked = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendTrackedMessage(e);
    }
  };

  const handleFeatureRequestClick = (e) => {
    e.preventDefault();
    const target = '/feature-requests';
    if (user) {
      navigate(target);
    } else {
      navigate('/login', { state: { from: { pathname: target } } });
    }
  };

  const switchToTickets = () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/contact?tab=tickets' } } });
      return;
    }
    setActiveTab('tickets');
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Render attachments inside a message bubble
  const renderAttachments = (msgAttachments) => {
    if (!msgAttachments || msgAttachments.length === 0) return null;
    return (
      <div className="msg-attachments">
        {msgAttachments.map((att, idx) => (
          <a
            key={idx}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="msg-attachment-item"
            download={att.name}
          >
            <span className="msg-attachment-icon">
              {att.type?.startsWith('image/') ? 'Image' : att.type?.startsWith('video/') ? 'Video' : att.type === 'application/pdf' ? 'PDF' : 'File'}
            </span>
            <span className="msg-attachment-info">
              <span className="msg-attachment-name">{att.name}</span>
              <span className="msg-attachment-size">{formatFileSize(att.size)}</span>
            </span>
            <span className="msg-attachment-dl">⬇</span>
          </a>
        ))}
      </div>
    );
  };

  // Render the file preview strip for pending attachments
  const renderPendingAttachments = (files, setFilesFn) => {
    if (files.length === 0) return null;
    return (
      <div className="pending-attachments">
        {files.map((file, idx) => (
          <div key={idx} className="pending-attachment-item">
            <span className="pending-att-icon">
              {file.type?.startsWith('image/') ? 'Image' : file.type?.startsWith('video/') ? 'Video' : file.type === 'application/pdf' ? 'PDF' : 'File'}
            </span>
            <span className="pending-att-name">{file.name}</span>
            <span className="pending-att-size">{formatFileSize(file.size)}</span>
            <button
              type="button"
              className="pending-att-remove"
              onClick={() => setFilesFn(prev => prev.filter((_, i) => i !== idx))}
              title="Remove"
            >✕</button>
          </div>
        ))}
      </div>
    );
  };

  const statusLabel = (status) => {
    const labels = {
      new: 'New', in_progress: 'In Progress', waiting: 'Awaiting Reply',
      resolved: 'Resolved', closed: 'Closed',
    };
    return labels[status] || status;
  };

  const statusClass = (status) => {
    const classes = {
      new: 'status-new', in_progress: 'status-progress', waiting: 'status-waiting',
      resolved: 'status-resolved', closed: 'status-closed',
    };
    return classes[status] || '';
  };

  // ── Success screen after submission ──
  if (submitted) {
    return (
      <>
        <div className="sl-page contact-page">
          <div className="pageheader borderleft">
            <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Contact' }]} />
            <div className="pageheading">Contact Us</div>
          </div>

          <div className="contact-content borderleft">
            <div className="success-message">
              <h2>Message Sent!</h2>
              <p>Thank you for reaching out. We'll get back to you as soon as possible.</p>
              
              {submittedData && (
                <div style={{ background: '#f0f7ff', padding: '16px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left', marginLeft: 'auto', marginRight: 'auto', maxWidth: '400px' }}>
                  <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#0369a1', fontWeight: 600 }}>Track Your Ticket</p>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.9rem', color: '#1f2937', marginBottom: '10px', wordwrapbreak: 'break-all' }}>
                    <div style={{ marginBottom: '8px' }}><strong>Ticket:</strong> <span style={{ color: '#6366f1' }}>{submittedData.ticketId}</span></div>
                    <div><strong>Code:</strong> <span style={{ color: '#6366f1' }}>{submittedData.trackingCode}</span></div>
                  </div>
                  <p style={{ margin: '0', fontSize: '0.8rem', color: '#0369a1' }}>
                    A confirmation email has been sent to <strong>{submittedData.email}</strong>
                  </p>
                </div>
              )}

              {user && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '20px' }}>
                  You can track your ticket and see our replies in the <strong>My Tickets</strong> tab below.
                </p>
              )}
              <div className="success-actions">
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setSubmittedData(null);
                    setFormData({
                      name: user?.displayName || '',
                      email: user?.email || '',
                      subject: '',
                      category: 'general',
                      message: '',
                    });
                  }}
                >
                  Send Another Message
                </button>
                {user && (
                  <button onClick={() => { setSubmitted(false); setActiveTab('tickets'); }} className="view-tickets-btn">
                    View my tickets
                  </button>
                )}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  // ── Main render ──
  return (
    <>
      <div className="sl-page contact-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Contact' }]} />
          <div className="pageheading">Contact Us</div>
        </div>

        <p className="privacy-hero__tagline mypadding borderleft">
          Have questions, feedback, or need help? We'd love to hear from you!
        </p>

        {/* Tabs */}
        <div className="contact-tabs borderleft">
          <button
            className={`contact-tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact us
          </button>
          <button
            className={`contact-tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={switchToTickets}
          >
            My tickets
            {user && tickets.some(t => t.hasUnreadReply) && <span className="tab-unread-dot"></span>}
          </button>
          <button
            className={`contact-tab ${activeTab === 'tracking' ? 'active' : ''}`}
            onClick={() => setActiveTab('tracking')}
          >
            Track a ticket
          </button>
        </div>

        {/* ───── Contact Tab ───── */}
        {activeTab === 'contact' && (
          <div className="contact-content borderleft">
            <div className="contact-body">
              {/* Contact Form */}
              <div className="contact-form-section">
                <h2>Send us a Message</h2>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text" id="name" name="name"
                        value={formData.name} onChange={handleChange}
                        placeholder="Your name" required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email" id="email" name="email"
                        value={formData.email} onChange={handleChange}
                        placeholder="your@email.com" required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="category">Category</label>
                      <select id="category" name="category" value={formData.category} onChange={handleChange}>
                        <option value="general">General Inquiry</option>
                        <option value="support">Technical Support</option>
                        <option value="billing">Billing Question</option>
                        <option value="feature">Feature Request</option>
                        <option value="bug">Bug Report</option>
                        <option value="partnership">Partnership</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>
                      <input
                        type="text" id="subject" name="subject"
                        value={formData.subject} onChange={handleChange}
                        placeholder="What's this about?"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message" name="message"
                      value={formData.message} onChange={handleChange}
                      placeholder="Tell us how we can help..." rows={6} required
                    />
                  </div>

                  {/* Attachments */}
                  <div className="form-group">
                    <label>Attachments <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.85em' }}>(optional — images, videos, PDF, max 5 MB each)</span></label>
                    <input
                      type="file"
                      ref={contactFileInputRef}
                      style={{ display: 'none' }}
                      multiple
                      accept="image/*,video/*,.pdf"
                      onChange={(e) => { handleFileSelect(e.target.files, setContactAttachments); e.target.value = ''; }}
                    />
                    <button
                      type="button"
                      className="attach-btn contact-attach-btn"
                      onClick={() => contactFileInputRef.current?.click()}
                      disabled={contactAttachments.length >= 5}
                    >
                      Add Files {contactAttachments.length > 0 && `(${contactAttachments.length}/5)`}
                    </button>
                    {contactAttachments.length > 0 && (
                      <div className="pending-attachments" style={{ marginTop: '10px' }}>
                        {contactAttachments.map((file, i) => (
                          <div key={i} className="pending-attachment-item">
                            <span className="pending-att-name">{file.name}</span>
                            <span className="pending-att-size">({(file.size / 1024).toFixed(0)} KB)</span>
                            <button
                              type="button"
                              className="pending-att-remove"
                              onClick={() => setContactAttachments(prev => prev.filter((_, idx) => idx !== i))}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? (contactAttachments.length > 0 ? 'Uploading & Sending...' : 'Sending...') : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="contact-info-section">
                <h2>Other Ways to Reach Us</h2>

                <div className="contact-cards">
                  <div className="contact-card">
                    <h3>Email</h3>
                    <p>pranavshekhawat2@gmail.com</p>
                    <a href="mailto:pranavshekhawat2@gmail.com">Send Email</a>
                  </div>

                  <div className="contact-card">
                    <h3>Community Forum</h3>
                    <p>Get help from other soap makers</p>
                    <a href="/community">Visit Forum</a>
                  </div>

                  <div className="contact-card">
                    <h3>Help Center</h3>
                    <p>Browse FAQs and guides</p>
                    <a href="/help">View Help Center</a>
                  </div>

                  <div className="contact-card">
                    <h3>Request a Feature</h3>
                    <p>Have an idea that would help you?</p>
                    <a href="/feature-requests" onClick={handleFeatureRequestClick}>
                      Request Feature
                    </a>
                  </div>
                </div>

                <div className="response-time">
                  <h3>Response Time</h3>
                  <p>We typically respond within 24-48 hours during business days.</p>
                  <p><strong>Pro members</strong> receive priority support.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ───── My Tickets Tab ───── */}
        {activeTab === 'tickets' && user && (
          <div className="contact-content borderleft">
            {ticketsLoading ? (
              <div className="tickets-loading">
                <div className="loading-spinner"></div>
                <p>Loading your tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="tickets-empty">
                <h3>No tickets yet</h3>
                <p>When you submit a contact form, your tickets will appear here.</p>
                <button onClick={() => setActiveTab('contact')} className="btn-primary">
                  Send a Message
                </button>
              </div>
            ) : (
              <div className="tickets-layout">
                {/* Ticket List */}
                <div className={`tickets-sidebar ${selectedTicket ? 'hide-on-mobile' : ''}`}>
                  <div className="sidebar-header">
                    <h3>Your Tickets ({tickets.length})</h3>
                    <button onClick={() => setActiveTab('contact')} className="new-ticket-btn">
                      + New
                    </button>
                  </div>
                  <div className="tickets-list">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className={`ticket-item ${selectedTicket?.id === ticket.id ? 'active' : ''} ${ticket.hasUnreadReply ? 'has-unread' : ''}`}
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        <div className="ticket-item-header">
                          <span className="ticket-item-subject">
                            {ticket.subject || 'No Subject'}
                          </span>
                          {ticket.hasUnreadReply && <span className="unread-dot"></span>}
                        </div>
                        <div className="ticket-item-meta">
                          <span className={`ticket-status-pill ${statusClass(ticket.status)}`}>
                            {ticket.status?.replace('_', ' ') || 'new'}
                          </span>
                          <span className="ticket-item-date">
                            {ticket.createdAt?.toDate?.()?.toLocaleDateString() || ''}
                          </span>
                        </div>
                        {ticket.ticketId && (
                          <span className="ticket-item-id">{ticket.ticketId}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Conversation View */}
                <div className={`ticket-conversation ${selectedTicket ? 'show-on-mobile' : ''}`}>
                  {selectedTicket ? (
                    <>
                      <div className="conversation-header">
                        <div className="conv-header-info">
                          <h3>{selectedTicket.subject || 'No Subject'}</h3>
                          <div className="conv-header-meta">
                            {selectedTicket.ticketId && (
                              <span className="conv-ticket-id">{selectedTicket.ticketId}</span>
                            )}
                            <span className={`ticket-status-pill ${statusClass(selectedTicket.status)}`}>
                              {statusLabel(selectedTicket.status)}
                            </span>
                            <span className="conv-category">
                              {selectedTicket.category || 'General'}
                            </span>
                          </div>
                        </div>
                        <button className="back-btn-mobile" onClick={() => setSelectedTicket(null)}>
                          Back
                        </button>
                      </div>

                      <div className="messages-area" ref={messagesAreaRef}>
                        <div className="message-bubble user-message">
                          <div className="message-sender">You</div>
                          <div className="message-text">{selectedTicket.message}</div>
                          <div className="message-time">
                            {selectedTicket.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}
                          </div>
                        </div>

                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`message-bubble ${msg.sender === 'admin' ? 'admin-message' : 'user-message'}`}
                          >
                            <div className="message-sender">
                              {msg.sender === 'admin' ? 'SoapLab Support' : 'You'}
                            </div>
                            <div className="message-text">{msg.text}</div>
                            {renderAttachments(msg.attachments)}
                            <div className="message-time">
                              {msg.createdAt?.toDate?.()?.toLocaleString() || 'Sending...'}
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>

                      {selectedTicket.status !== 'closed' ? (
                        <>
                        {renderPendingAttachments(attachments, setAttachments)}
                        <div className="reply-input-area">
                          <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="image/*,video/*,.pdf"
                            multiple
                            onChange={(e) => { handleFileSelect(e.target.files, setAttachments); e.target.value = ''; }}
                          />
                          <button
                            type="button"
                            className="attach-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending || attachments.length >= 5}
                            title="Attach file (max 5 MB, up to 5 files)"
                          >
                            📎
                          </button>
                          <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={uploading ? 'Uploading attachments...' : 'Type your reply... (Enter to send)'}
                            rows={2}
                            disabled={sending}
                          />
                          <button
                            onClick={handleSendMessage}
                            disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                            className="send-btn"
                          >
                            {sending ? '...' : '➤'}
                          </button>
                        </div>
                        </>
                      ) : (
                        <div className="ticket-closed-banner">
                          This ticket is closed.{' '}
                          <button onClick={() => setActiveTab('contact')}>Open a new ticket</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-ticket-selected">
                      <span className="empty-conv-icon">💬</span>
                      <h3>Select a ticket</h3>
                      <p>Choose a ticket from the left to view the conversation.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ───── Track Ticket Tab ───── */}
        {activeTab === 'tracking' && (
          <div className="contact-content borderleft">
            {!trackedTicket ? (
              <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <form onSubmit={handleSearchTrackedTicket} className="contact-form" style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                  <h2 style={{ marginBottom: '24px', fontSize: '1.3rem' }}>Find Your Ticket</h2>

                  {trackingError && <div className="error-message">{trackingError}</div>}

                  <div className="form-group">
                    <label htmlFor="track-email">Email Address *</label>
                    <input
                      type="email"
                      id="track-email"
                      value={trackingEmail}
                      onChange={(e) => setTrackingEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="track-code">Tracking Code *</label>
                    <input
                      type="text"
                      id="track-code"
                      value={trackingCode}
                      onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                      placeholder="e.g., ABC123"
                      maxLength={6}
                      required
                      style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '0.15em' }}
                    />
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '6px 0 0' }}>
                      Check your confirmation email for this code
                    </p>
                  </div>

                  <button type="submit" className="submit-btn" disabled={trackingLoading}>
                    {trackingLoading ? 'Searching...' : 'Find Ticket'}
                  </button>
                </form>

                <div style={{ marginTop: '32px', padding: '20px', background: '#f0f7ff', borderRadius: '12px' }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: '#0369a1' }}>Don't have your tracking code?</h3>
                  <p style={{ margin: '0', fontSize: '0.95rem', color: '#0c4a6e' }}>
                    Check the confirmation email we sent you when you submitted your message. It contains both your Ticket ID and Tracking Code.
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ background: 'white', padding: '24px', borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px', fontSize: '1.3rem' }}>{trackedTicket.subject || 'No Subject'}</h2>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className={`ticket-status-pill ${statusClass(trackedTicket.status)}`}>
                        {statusLabel(trackedTicket.status)}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'monospace' }}>
                        {trackedTicket.ticketId}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {trackedTicket.category || 'General'}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                        {trackedTicket.createdAt?.toDate?.()?.toLocaleString() || ''}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setTrackedTicket(null);
                      setTrackedMessages([]);
                      setTrackingEmail('');
                      setTrackingCode('');
                      setTrackingError(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#f3f4f6',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    ← Search Another
                  </button>
                </div>

                <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#6b7280', fontWeight: 600 }}>Your Message:</h3>
                  <p style={{ margin: 0, fontSize: '0.95rem', color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                    {trackedTicket.message}
                  </p>
                </div>

                <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <div className="messages-area" ref={trackedMessagesAreaRef} style={{ minHeight: '300px' }}>
                    {trackedMessages.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', textAlign: 'center' }}>
                        <div>
                          <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>💬</div>
                          <p style={{ margin: 0, fontSize: '0.95rem' }}>
                            No responses yet. We'll reply to you via email when we have an update.
                          </p>
                        </div>
                      </div>
                    ) : (
                      trackedMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`message-bubble ${msg.sender === 'admin' ? 'admin-message' : 'user-message'}`}
                        >
                          <div className="message-sender">
                            {msg.sender === 'admin' ? 'SoapLab Support' : 'You'}
                          </div>
                          <div className="message-text">{msg.text}</div>
                          {renderAttachments(msg.attachments)}
                          <div className="message-time">
                            {msg.createdAt?.toDate?.()?.toLocaleString() || 'Sending...'}
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={trackedMessagesEndRef} />
                  </div>

                  {trackedTicket.status !== 'closed' && (
                    <>
                    {renderPendingAttachments(trackedAttachments, setTrackedAttachments)}
                    <div className="reply-input-area">
                      <input
                        type="file"
                        ref={trackedFileInputRef}
                        style={{ display: 'none' }}
                        accept="image/*,video/*,.pdf"
                        multiple
                        onChange={(e) => { handleFileSelect(e.target.files, setTrackedAttachments); e.target.value = ''; }}
                      />
                      <button
                        type="button"
                        className="attach-btn"
                        onClick={() => trackedFileInputRef.current?.click()}
                        disabled={trackedSending || trackedAttachments.length >= 5}
                        title="Attach file (max 5 MB, up to 5 files)"
                      >
                        📎
                      </button>
                      <textarea
                        value={trackedNewMessage}
                        onChange={(e) => setTrackedNewMessage(e.target.value)}
                        onKeyDown={handleKeyDownTracked}
                        placeholder={trackedUploading ? 'Uploading attachments...' : 'Type your reply... (Enter to send)'}
                        rows={2}
                        disabled={trackedSending}
                      />
                      <button
                        onClick={handleSendTrackedMessage}
                        disabled={trackedSending || (!trackedNewMessage.trim() && trackedAttachments.length === 0)}
                        className="send-btn"
                      >
                        {trackedSending ? '...' : '➤'}
                      </button>
                    </div>
                    </>
                  )}

                  {trackedTicket.status === 'closed' && (
                    <div className="ticket-closed-banner">
                      This ticket is closed. You can no longer reply. Please contact us if you need further assistance.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}